import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../services/adminService';

const NewsManagement = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    categoria: 'geral',
    ativo: true
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedItems, setSelectedItems] = useState([]);

  const categories = [
    { value: 'geral', label: 'Geral' },
    { value: 'comunicados', label: 'Comunicados' },
    { value: 'eventos', label: 'Eventos' },
    { value: 'promocoes', label: 'Promoções' },
    { value: 'outros', label: 'Outros' }
  ];

  const loadNews = useCallback(async () => {
    setLoading(true);
    try {
      const items = await adminService.getNews();
      setNews(items);
    } catch (error) {
      console.error('Error loading news:', error);
      showToast('Erro ao carregar notícias', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length === 0) return;
    const newPreviews = newFiles.map(f => URL.createObjectURL(f));
    setImageFiles(prev => [...prev, ...newFiles]);
    setImagePreviews(prev => [...prev, ...newPreviews]);
    e.target.value = '';
  };

  const handleRemoveImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Use native news categories directly
      const normalizedCategoria = formData.categoria || 'geral';

      if (editingItem) {
        // Update metadata only; image replacement is not directly supported by backend
        await adminService.updateNews(editingItem._id, {
          titulo: formData.titulo,
          descricao: formData.descricao,
          categoria: normalizedCategoria
        });
        showToast('Notícia atualizada com sucesso!', 'success');
      } else {
        // Create news with or without images
        if (imageFiles.length > 0) {
          const fd = new FormData();
          imageFiles.forEach(file => fd.append('images', file));
          fd.append('titulo', formData.titulo);
          if (formData.descricao) fd.append('descricao', formData.descricao);
          fd.append('categoria', normalizedCategoria);
          fd.append('isActive', formData.ativo ? 'true' : 'false');

          const result = await adminService.uploadNewsImage(fd);
          if (!result?.success) {
            throw new Error(result?.message || 'Falha no upload das imagens');
          }
        } else {
          await adminService.createNews({
            titulo: formData.titulo,
            descricao: formData.descricao,
            categoria: normalizedCategoria,
            isActive: !!formData.ativo
          });
        }
        showToast('Notícia adicionada com sucesso!', 'success');
      }
      resetForm();
      loadNews();
    } catch (error) {
      console.error('Error saving news:', error);
      showToast(error.message || 'Erro ao salvar notícia', 'error');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      titulo: item.titulo,
      descricao: item.descricao || '',
      categoria: item.categoria || 'geral',
      ativo: item.isActive !== false
    });
    setImageFiles([]);
    setImagePreviews([]);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta notícia?')) {
      return;
    }
    try {
      await adminService.deleteNews(id);
      showToast('Notícia excluída com sucesso!', 'success');
      loadNews();
    } catch (error) {
      console.error('Error deleting news:', error);
      showToast(error.message || 'Erro ao excluir notícia', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    if (!window.confirm(`Tem certeza que deseja excluir ${selectedItems.length} notícia(s)?`)) {
      return;
    }
    try {
      await Promise.all(selectedItems.map(id => adminService.deleteNews(id)));
      showToast(`${selectedItems.length} notícia(s) excluída(s) com sucesso!`, 'success');
      setSelectedItems([]);
      loadNews();
    } catch (error) {
      console.error('Error bulk deleting news:', error);
      showToast('Erro ao excluir notícias', 'error');
    }
  };

  const handleItemSelect = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectAll = () => {
    const filteredIds = filteredItems.map(item => item._id);
    setSelectedItems(prev => prev.length === filteredIds.length ? [] : filteredIds);
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      descricao: '',
      categoria: 'geral',
      ativo: true
    });
    imagePreviews.forEach(url => URL.revokeObjectURL(url));
    setImageFiles([]);
    setImagePreviews([]);
    setEditingItem(null);
    setShowForm(false);
  };

  const showToast = (message, type = 'success') => {
    const toast = document.createElement('div');
    const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';
    const icon = type === 'success' ? 'ph-check-circle' : 'ph-x-circle';
    toast.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform translate-x-full transition-transform`;
    toast.innerHTML = `
      <div class="flex items-center">
        <i class="ph ${icon} text-lg mr-2"></i>
        <span>${message}</span>
      </div>
    `;
    document.body.appendChild(toast);
    setTimeout(() => { toast.classList.remove('translate-x-full'); }, 100);
    setTimeout(() => {
      toast.classList.add('translate-x-full');
      setTimeout(() => { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 300);
    }, 3000);
  };

  const filteredItems = news.filter(item => filter === 'all' || item.categoria === filter);
  const sortedFilteredItems = [...filteredItems].sort((a, b) => {
    const aDate = new Date(a.createdAt || 0).getTime();
    const bDate = new Date(b.createdAt || 0).getTime();
    return bDate - aDate; // newest first
  });
  const groupedItems = sortedFilteredItems.reduce((acc, item) => {
    const key = item.categoria || 'geral';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Gerenciamento de Notícias</h2>
            <p className="text-gray-600 mt-1">Cadastre e gerencie as notícias publicadas</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg flex items-center transition-colors"
          >
            <i className="ph ph-plus mr-2"></i>
            Adicionar Notícia
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{news.length}</div>
            <div className="text-sm text-blue-800">Total de Notícias</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {news.filter(item => item.isActive !== false).length}
            </div>
            <div className="text-sm text-green-800">Ativas</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              {news.filter(item => item.categoria === 'eventos').length}
            </div>
            <div className="text-sm text-yellow-800">Eventos</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {news.filter(item => item.categoria === 'comunicados').length}
            </div>
            <div className="text-sm text-purple-800">Comunicados</div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'all' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todas ({news.length})
            </button>
            {categories.map(category => {
              const count = news.filter(item => item.categoria === category.value).length;
              return (
                <button
                  key={category.value}
                  onClick={() => setFilter(category.value)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    filter === category.value ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.label} ({count})
                </button>
              );
            })}
          </div>

          {selectedItems.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {selectedItems.length} selecionada(s)
              </span>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded transition-colors"
              >
                <i className="ph ph-trash mr-1"></i>
                Excluir
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingItem ? 'Editar Notícia' : 'Adicionar Notícia'}
                </h3>
                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                  <i className="ph ph-x text-2xl"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Título *
                    </label>
                    <input
                      type="text"
                      name="titulo"
                      value={formData.titulo}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Categoria
                    </label>
                    <select
                      name="categoria"
                      value={formData.categoria}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Conteúdo / Descrição
                  </label>
                  <textarea
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="Texto da notícia"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Imagens da Notícia (opcional)
                    {imageFiles.length > 0 && (
                      <span className="ml-2 text-xs text-gray-500">({imageFiles.length} selecionada{imageFiles.length > 1 ? 's' : ''})</span>
                    )}
                  </label>
                  <div className="rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/40 hover:bg-amber-50 p-4 sm:p-6 transition-colors">
                    <div className="flex items-center justify-center text-center">
                      <div className="space-y-3">
                        <div className="text-3xl">📷</div>
                        <p className="text-sm text-gray-700">Selecione uma ou mais imagens</p>
                        <p className="text-xs text-gray-500">A primeira será usada como capa</p>
                        <label className="inline-flex items-center px-3 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageChange}
                            className="hidden"
                          />
                          {imageFiles.length > 0 ? 'Adicionar mais imagens' : 'Selecionar imagens'}
                        </label>
                        <p className="text-xs text-gray-500">Máx 25MB por arquivo · JPG · PNG · WEBP · GIF · até 10 imagens</p>
                      </div>
                    </div>

                    {imagePreviews.length > 0 && (
                      <div className="mt-4">
                        <div className="text-xs text-gray-600 mb-2">Pré-visualização</div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                          {imagePreviews.map((url, idx) => (
                            <div key={url} className="relative group">
                              <img
                                src={url}
                                alt={`Pré-visualização ${idx + 1}`}
                                className="w-full h-28 object-cover rounded-lg border border-gray-200 shadow-sm"
                              />
                              {idx === 0 && (
                                <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[10px] font-medium bg-amber-500 text-white rounded">
                                  Capa
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => handleRemoveImage(idx)}
                                className="absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remover imagem"
                              >
                                <i className="ph ph-x text-xs"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {editingItem && imagePreviews.length === 0 && (() => {
                      const existing = (editingItem.images && editingItem.images.length > 0)
                        ? editingItem.images.map(i => i.url)
                        : (editingItem.url ? [editingItem.url] : []);
                      if (existing.length === 0) return null;
                      return (
                        <div className="mt-4">
                          <div className="text-xs text-gray-600 mb-2">Imagens atuais ({existing.length})</div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {existing.map((url, idx) => (
                              <img
                                key={url + idx}
                                src={url}
                                alt={`Imagem ${idx + 1}`}
                                className="w-full h-28 object-cover rounded-lg border border-gray-200 shadow-sm opacity-70"
                              />
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 mt-2 italic">Edição de imagens não disponível — apenas metadados.</p>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center pt-6">
                    <input
                      type="checkbox"
                      name="ativo"
                      checked={formData.ativo}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">
                      Notícia ativa (visível)
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button type="button" onClick={resetForm} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                    Cancelar
                  </button>
                  <button type="submit" className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors">
                    {editingItem ? 'Atualizar' : 'Adicionar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* News List */}
      <div className="space-y-6">
        {filter === 'all' ? (
          categories.map(category => {
            const categoryItems = (groupedItems[category.value] || []).sort((a, b) => {
              const aDate = new Date(a.createdAt || 0).getTime();
              const bDate = new Date(b.createdAt || 0).getTime();
              return bDate - aDate;
            });
            if (categoryItems.length === 0) return null;
            return (
              <div key={category.value} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {category.label} ({categoryItems.length})
                  </h3>
                  <button onClick={handleSelectAll} className="text-sm text-amber-600 hover:text-amber-700">
                    {selectedItems.length === filteredItems.length ? 'Desmarcar' : 'Selecionar'} Todas
                  </button>
                </div>
                <div className="space-y-4">
                  {categoryItems.map(item => (
                    <div key={item._id} className="relative group border border-gray-200 rounded-lg p-4">
                      <div className="flex gap-4">
                        <div className="w-28 h-28 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                          {item.url ? (
                            <img src={item.url} alt={item.titulo} className="w-full h-full object-cover" />
                          ) : (
                            <i className="ph ph-newspaper text-3xl text-gray-400"></i>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <h4 className="text-sm font-medium text-gray-900 truncate">{item.titulo}</h4>
                            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => handleEdit(item)} className="p-2 bg-white border border-gray-200 rounded-full text-blue-600 hover:bg-blue-50" title="Editar">
                                <i className="ph ph-pencil"></i>
                              </button>
                              <button onClick={() => handleDelete(item._id)} className="p-2 bg-white border border-gray-200 rounded-full text-red-600 hover:bg-red-50" title="Excluir">
                                <i className="ph ph-trash"></i>
                              </button>
                            </div>
                          </div>
                          {item.descricao && (
                            <p className="text-xs text-gray-600 mt-1 line-clamp-2">{item.descricao}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end justify-between">
                          <div className="">
                            <input type="checkbox" checked={selectedItems.includes(item._id)} onChange={() => handleItemSelect(item._id)} className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded" />
                          </div>
                          {item.isActive === false && (
                            <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">Inativa</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {categories.find(c => c.value === filter)?.label} ({filteredItems.length})
              </h3>
              <button onClick={handleSelectAll} className="text-sm text-amber-600 hover:text-amber-700">
                {selectedItems.length === filteredItems.length ? 'Desmarcar' : 'Selecionar'} Todas
              </button>
            </div>
            <div className="space-y-4">
              {filteredItems.map(item => (
                <div key={item._id} className="relative group border border-gray-200 rounded-lg p-4">
                  <div className="flex gap-4">
                    <div className="w-28 h-28 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                      {item.url ? (
                        <img src={item.url} alt={item.titulo} className="w-full h-full object-cover" />
                      ) : (
                        <i className="ph ph-newspaper text-3xl text-gray-400"></i>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <h4 className="text-sm font-medium text-gray-900 truncate">{item.titulo}</h4>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleEdit(item)} className="p-2 bg-white border border-gray-200 rounded-full text-blue-600 hover:bg-blue-50" title="Editar">
                            <i className="ph ph-pencil"></i>
                          </button>
                          <button onClick={() => handleDelete(item._id)} className="p-2 bg-white border border-gray-200 rounded-full text-red-600 hover:bg-red-50" title="Excluir">
                            <i className="ph ph-trash"></i>
                          </button>
                        </div>
                      </div>
                      {item.descricao && (
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{item.descricao}</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <div className="">
                        <input type="checkbox" checked={selectedItems.includes(item._id)} onChange={() => handleItemSelect(item._id)} className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded" />
                      </div>
                      {item.isActive === false && (
                        <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">Inativa</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {filteredItems.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <i className="ph ph-newspaper text-4xl text-gray-400 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {filter === 'all' ? 'Nenhuma notícia adicionada' : `Nenhuma notícia em ${categories.find(c => c.value === filter)?.label}`}
          </h3>
          <p className="text-gray-600 mb-4">Comece adicionando notícias.</p>
          <button onClick={() => setShowForm(true)} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg transition-colors">
            Adicionar Primeira Notícia
          </button>
        </div>
      )}
    </div>
  );
};

export default NewsManagement;